import React, { useMemo, useState } from 'react';
import { type ColumnDef } from '@tanstack/react-table';
import {
  ConfirmationDialog,
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@roger/ui-components';
import BillingPeriodCell from '../BillingPeriodCell';
import {
  getActiveBillingCycles,
  setBillingCycleStatusPaid,
  setBillingCycleStatusRejected,
  setBillingCycleStatusVoid,
} from '../../../services/api';
import type {
  ActiveBillingCycleItem,
  EpisodicClaimStatus,
} from '../../../services/api/types.gen';
import { formatDateToMMDDYYYY } from '../../../utils/dateUtils';
import { ClaimAgeCell } from '../ClaimAgeCell';
import { claimAgeDays } from '../claimAge';
import { useBillingCycles } from '../../../hooks/useBillingCycles';
import { useBillingTableQuery } from '../../../hooks/useBillingTableQuery';
import { BillingDataTable } from '../BillingDataTable';
import { BillingSelectionActionBar } from '../BillingSelectionActionBar';
import { activeBillingCycleNoteContext } from '../billingCycleNoteContext';
import { currencySortingFn } from '../billingTableUtils';
import { showVoidBillingCycleFailures } from '../showVoidBillingCycleFailures';
import { PatientCell } from '../PatientCell';
import { InsuranceCell } from '../InsuranceCell';
import { BillingActionsCell } from '../BillingActionsCell';
import { ClaimWorksheetViewerAction } from '../ClaimWorksheetViewerAction';
import {
  VoidClaimsConfirmDialog,
  type VoidCopyOptions,
} from '../VoidClaimsButton';

interface EOEPendingPaymentTabProps {
  onCountsInvalidated?: () => Promise<void> | void;
}

const EOEPendingPaymentTab: React.FC<EOEPendingPaymentTabProps> = ({
  onCountsInvalidated,
}) => {
  const [pushIdsOpen, setPushIdsOpen] = useState(false);
  const [paidConfirmOpen, setPaidConfirmOpen] = useState(false);
  const [rejectedConfirmOpen, setRejectedConfirmOpen] = useState(false);
  const [voidConfirmOpen, setVoidConfirmOpen] = useState(false);

  const query = useBillingTableQuery('pending-payment', {
    sortKeyByColumnId: { billing_period: 'billing_period_start' },
  });
  const {
    data,
    isLoading,
    isRefreshing,
    isSubmitting,
    selectedIds,
    selectedItems,
    total,
    totalPages,
    clearSelection,
    removeSelectedId,
    handleToggleId,
    handleToggleAll,
    mutateSelected,
    showError,
  } = useBillingCycles<
    ActiveBillingCycleItem,
    { statuses: EpisodicClaimStatus[] }
  >({
    tabId: 'pending-payment',
    fetchFn: getActiveBillingCycles,
    fetchPayload: { statuses: ['PENDING_PAYMENT'], ...query.toParams() },
    errorMessage: 'Could not fetch data for the "Pending Payment" tab.',
    getId: item => item.billing_cycle_id,
    getName: item => item.patient_name,
  });

  const handleConfirmPaid = () => {
    setPaidConfirmOpen(false);
    void mutateSelected(
      id =>
        setBillingCycleStatusPaid({
          path: { billing_cycle_id: id },
        }),
      'Some billing cycles could not be marked as paid',
      'The following billing cycles could not be updated',
      onCountsInvalidated
    );
  };

  const handleVoid = (options: VoidCopyOptions) => {
    void mutateSelected(
      id =>
        setBillingCycleStatusVoid({
          path: { billing_cycle_id: id },
          body: {
            copy_worksheet_forward: options.copyWorksheetForward,
            copy_notes_forward: options.copyNotesForward,
          },
        }),
      'Some billing cycles failed to void',
      'The following billing cycles could not be updated',
      onCountsInvalidated,
      failures => showVoidBillingCycleFailures(failures, showError)
    );
  };

  const handleConfirmRejected = () => {
    setRejectedConfirmOpen(false);
    void mutateSelected(
      id =>
        setBillingCycleStatusRejected({
          path: { billing_cycle_id: id },
        }),
      'Some billing cycles could not be marked as rejected',
      'The following billing cycles could not be updated',
      onCountsInvalidated
    );
  };

  const columns = useMemo<ColumnDef<ActiveBillingCycleItem>[]>(
    () => [
      {
        id: 'patient_name',
        accessorKey: 'patient_name',
        header: 'Patient',
        enableSorting: true,
        cell: ({ row }) => (
          <PatientCell
            name={row.original.patient_name}
            mrn={row.original.patient_mrn}
          />
        ),
      },
      {
        id: 'branch',
        accessorKey: 'branch',
        header: 'Clinic',
        enableSorting: true,
      },
      {
        id: 'insurance_name',
        accessorKey: 'insurance_name',
        header: 'Insurance',
        enableSorting: true,
        cell: ({ row }) => (
          <InsuranceCell
            insuranceName={row.original.insurance_name}
            mbi={row.original.mbi}
            isMedicare={row.original.is_medicare}
          />
        ),
      },
      {
        id: 'billing_period',
        header: 'Billing Period',
        accessorFn: row => row.billing_period_start,
        enableSorting: true,
        cell: ({ row }) => (
          <BillingPeriodCell
            patientId={row.original.patient_id}
            episodeId={row.original.episode_id}
            startDate={formatDateToMMDDYYYY(row.original.billing_period_start)}
            endDate={formatDateToMMDDYYYY(row.original.billing_period_end)}
          />
        ),
      },
      {
        id: 'claim_number',
        accessorKey: 'claim_number',
        header: 'Claim #',
        enableSorting: true,
      },
      {
        id: 'total_charges',
        accessorKey: 'total_charges',
        header: 'Total Charges',
        enableSorting: false,
        sortingFn: currencySortingFn,
      },
      {
        id: 'claim_age',
        header: 'Claim Age',
        accessorFn: row => claimAgeDays(row.billing_period_end) ?? -1,
        enableSorting: true,
        cell: ({ row }) => (
          <ClaimAgeCell
            periodEnd={row.original.billing_period_end}
            createdAt={row.original.created_at}
            billedAt={row.original.billed_at}
          />
        ),
      },
      {
        id: 'actions',
        header: () => <div className='text-right'>Actions</div>,
        enableSorting: false,
        cell: ({ row }) => (
          <BillingActionsCell
            billingCycleId={row.original.billing_cycle_id}
            noteProps={{
              ...activeBillingCycleNoteContext(row.original),
              patientName: row.original.patient_name,
            }}
            leading={<ClaimWorksheetViewerAction billingCycle={row.original} />}
          />
        ),
      },
    ],
    []
  );

  return (
    <div className='flex flex-col gap-4'>
      <BillingDataTable
        data={data}
        columns={columns}
        getRowId={row => row.billing_cycle_id}
        query={query}
        total={total}
        totalPages={totalPages}
        isLoading={isLoading}
        isRefreshing={isRefreshing}
        selection={{
          selectedIds,
          onToggleId: handleToggleId,
          onToggleAllVisible: handleToggleAll,
        }}
      />
      <Dialog open={pushIdsOpen} onOpenChange={setPushIdsOpen}>
        <DialogContent className='max-w-lg'>
          <DialogHeader>
            <DialogTitle>Selected billing cycle IDs</DialogTitle>
          </DialogHeader>
          <pre className='text-sm bg-muted rounded p-4 overflow-auto max-h-64 whitespace-pre-wrap break-all'>
            {Array.from(selectedIds).join('\n')}
          </pre>
        </DialogContent>
      </Dialog>
      <ConfirmationDialog
        open={paidConfirmOpen}
        title='Mark claims as paid?'
        message="Marks the selected claims as Paid. Use this when payment was received. This can't be undone."
        confirmLabel='Mark as Paid'
        onAccept={handleConfirmPaid}
        onCancel={() => setPaidConfirmOpen(false)}
      />
      <ConfirmationDialog
        open={rejectedConfirmOpen}
        title='Mark claims as rejected?'
        message="Marks the selected claims as Rejected. The billing cycle can't be resubmitted, and this can't be undone."
        confirmLabel='Mark as Rejected'
        onAccept={handleConfirmRejected}
        onCancel={() => setRejectedConfirmOpen(false)}
      />
      <VoidClaimsConfirmDialog
        open={voidConfirmOpen}
        selectedCount={selectedIds.size}
        postSend={true}
        onConfirm={options => {
          setVoidConfirmOpen(false);
          handleVoid(options);
        }}
        onCancel={() => setVoidConfirmOpen(false)}
      />
      <BillingSelectionActionBar
        selectedItems={selectedItems}
        onClear={clearSelection}
        onRemoveSelectedId={removeSelectedId}
        actions={[
          {
            key: 'mark-paid',
            label: 'Mark as Paid',
            disabledReason: () =>
              isSubmitting || isLoading ? 'Action in progress' : undefined,
            run: () => setPaidConfirmOpen(true),
          },
          {
            key: 'mark-rejected',
            label: 'Mark as Rejected',
            destructive: true,
            disabledReason: () =>
              isSubmitting || isLoading ? 'Action in progress' : undefined,
            run: () => setRejectedConfirmOpen(true),
          },
          {
            key: 'void',
            label: 'Void',
            destructive: true,
            disabledReason: () =>
              isSubmitting || isLoading ? 'Action in progress' : undefined,
            run: () => setVoidConfirmOpen(true),
          },
        ]}
      />
    </div>
  );
};

export default EOEPendingPaymentTab;
